import { useState } from 'react'
import { Bookmark, Check, Trash2 } from 'lucide-react'
import { Button, Popover, PopoverContent, PopoverTrigger } from './ui'
import { useSavedViews } from '../hooks/useSavedViews'
import { normalizeChannelColumns } from '../lib/channelReport'

const newViewId = () => `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

/** Same layout, ignoring columns added to the catalogue after the view was saved. */
export function sameLayout(view, preferences, normalize = normalizeChannelColumns) {
  const a = normalize(view)
  const b = normalize(preferences)
  return a.order.join() === b.order.join() && [...a.visible].sort().join() === [...b.visible].sort().join()
}

/** Saved column layouts for a report — `channels` unless another report says so. */
export default function ChannelViewPicker({ preferences, activeId, onApply, report = 'channels', normalize = normalizeChannelColumns }) {
  const { views, save, loading, error } = useSavedViews(report)
  const [name, setName] = useState('')
  const active = views.find(view => view.id === activeId) || null
  const modified = active && !sameLayout(active, preferences, normalize)

  const saveCurrent = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    // Saving under an existing name updates that view rather than adding a twin.
    const existing = views.find(view => view.name.toLowerCase() === trimmed.toLowerCase())
    const view = { id: existing?.id || newViewId(), name: trimmed, order: preferences.order, visible: preferences.visible }
    const next = existing ? views.map(item => item.id === existing.id ? view : item) : [...views, view]
    if (await save(next)) { onApply(preferences, view.id); setName('') }
  }
  const update = () => save(views.map(view => view.id === active.id ? { ...view, order: preferences.order, visible: preferences.visible } : view))
  const remove = async id => {
    if (await save(views.filter(view => view.id !== id)) && id === activeId) onApply(preferences, null)
  }

  return <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm"><Bookmark className="mr-2 h-4 w-4" />{active ? `${active.name}${modified ? ' *' : ''}` : 'Views'}</Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-80">
      <div className="mb-2 text-sm font-semibold">Saved views</div>
      <p className="mb-3 text-xs text-muted-foreground">Your own column layouts, saved to your account.</p>
      <div className="space-y-1">
        <button className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted/50" onClick={() => onApply(normalize(null), null)}>
          <Check className={`h-3.5 w-3.5 ${!active ? '' : 'invisible'}`} />Default layout
        </button>
        {loading && <p className="px-2 py-1 text-xs text-muted-foreground">Loading…</p>}
        {views.map(view => <div key={view.id} className="group flex items-center gap-1 rounded hover:bg-muted/50">
          <button className="flex flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm" onClick={() => onApply(normalize(view), view.id)}>
            <Check className={`h-3.5 w-3.5 ${view.id === activeId ? '' : 'invisible'}`} />{view.name}
          </button>
          <button className="rounded p-1 opacity-60 hover:bg-muted hover:opacity-100" aria-label={`Delete view ${view.name}`} onClick={() => remove(view.id)}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>)}
      </div>
      {modified && <Button size="sm" variant="secondary" className="mt-3 w-full" onClick={update}>Update “{active.name}” with current columns</Button>}
      <form className="mt-3 flex gap-2 border-t pt-3" onSubmit={event => { event.preventDefault(); saveCurrent() }}>
        <input className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm" maxLength={60} placeholder="Name this layout…" value={name} onChange={event => setName(event.target.value)} aria-label="View name" />
        <Button type="submit" size="sm" disabled={!name.trim()}>Save</Button>
      </form>
      {error && <p role="alert" className="mt-2 text-xs text-destructive">{error}</p>}
    </PopoverContent>
  </Popover>
}
